<template>
  <div class="message-prepare">
    <div class="summary-bar d-flex align-items-center mb-3">
      <div class="summary-item">
        <div class="summary-value">
          {{ payerCount }}
        </div>
        <div class="summary-label text-secondary">
          {{ $t('menu.payers') }}
        </div>
      </div>
      <div class="summary-item">
        <div class="summary-value">
          {{ totalBillsCount }}
        </div>
        <div class="summary-label text-secondary">
          {{ $t('bill.unpaidCount') }}
        </div>
      </div>
      <div class="summary-item">
        <div class="summary-value">
          {{ formatMoney(totalAmountDue, 'PLN') }}
        </div>
        <div class="summary-label text-secondary">
          {{ $t('bill.toPay') }}
        </div>
      </div>
      <button
        class="btn btn-sm btn-outline-secondary ml-auto"
        @click="$emit('close')"
      >
        <i class="far fa-arrow-left mr-1" />
        {{ $t('general.back') }}
      </button>
    </div>

    <hr class="mt-0 mb-3">

    <BoxSelect
      v-if="sortKey === 'name'"
      :fields="channelFields"
      :selected.sync="messageType"
      class="mb-3"
      style="max-width: 300px"
    />

    <hr
      v-if="sortKey === 'name'"
      class="mt-0 mb-3"
    >

    <div class="editor-layout">
      <div class="template-panel">
        <div class="template-panel-header">
          {{ $t('vindication.templates') }}
        </div>
        <div
          v-if="templates.length === 0"
          class="text-secondary small p-2"
        >
          {{ $t('vindication.noTemplates') }}
        </div>
        <div
          v-for="(tpl, idx) in filteredTemplates"
          :key="idx"
          class="template-item"
          :class="{ active: selectedTemplateIdx === idx }"
          @click="loadTemplate(tpl, idx)"
        >
          <div class="template-name">
            {{ tpl.name || tpl.subject || $t('vindication.untitledTemplate') }}
          </div>
          <div class="template-meta text-secondary">
            {{ tpl.type === 'email' ? 'Email' : 'SMS' }}
          </div>
          <button
            class="template-delete btn btn-sm"
            @click.stop="removeTemplate(idx)"
          >
            <i class="far fa-trash-alt" />
          </button>
        </div>
      </div>

      <div class="editor-panel">
        <div
          v-if="messageType === 'email'"
          class="mb-2"
        >
          <input
            v-model="subject"
            :placeholder="$t('vindication.subject')"
            class="form-control"
          >
        </div>

        <div class="toolbar">
          <button
            class="toolbar-btn"
            :class="{ active: editorActive('bold') }"
            @click="editorCommand('bold')"
          >
            <i class="fas fa-bold" />
          </button>
          <button
            class="toolbar-btn"
            :class="{ active: editorActive('italic') }"
            @click="editorCommand('italic')"
          >
            <i class="fas fa-italic" />
          </button>
          <button
            class="toolbar-btn"
            :class="{ active: editorActive('underline') }"
            @click="editorCommand('underline')"
          >
            <i class="fas fa-underline" />
          </button>
          <div class="toolbar-separator" />
          <button
            class="toolbar-btn"
            :class="{ active: editorActive('bulletList') }"
            @click="editorCommand('bulletList')"
          >
            <i class="fas fa-list-ul" />
          </button>
          <button
            class="toolbar-btn"
            :class="{ active: editorActive('orderedList') }"
            @click="editorCommand('orderedList')"
          >
            <i class="fas fa-list-ol" />
          </button>
          <div class="toolbar-separator" />
          <button
            class="toolbar-btn"
            :class="{ active: editorActive({ textAlign: 'left' }) }"
            @click="editorCommand('alignLeft')"
          >
            <i class="fas fa-align-left" />
          </button>
          <button
            class="toolbar-btn"
            :class="{ active: editorActive({ textAlign: 'center' }) }"
            @click="editorCommand('alignCenter')"
          >
            <i class="fas fa-align-center" />
          </button>
          <button
            class="toolbar-btn"
            :class="{ active: editorActive({ textAlign: 'right' }) }"
            @click="editorCommand('alignRight')"
          >
            <i class="fas fa-align-right" />
          </button>
        </div>

        <EditorContent
          :editor="editor"
          class="editor-content"
        />

        <div class="editor-actions mt-3 d-flex align-items-center">
          <button
            class="btn btn-outline-secondary mr-2"
            :disabled="savingTemplate || !canSend"
            @click="showSaveTemplate = true"
          >
            <i class="far fa-save mr-1" />
            {{ $t('vindication.saveTemplate') }}
          </button>
          <button
            class="btn btn-outline-secondary mr-2"
            @click="clearEditor"
          >
            <i class="far fa-eraser mr-1" />
            {{ $t('vindication.clear') }}
          </button>
          <button
            class="btn btn-primary ml-auto"
            :disabled="sending || !canSend"
            @click="send"
          >
            <i
              class="far mr-1"
              :class="sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'"
            />
            {{ $t('vindication.send') }}
          </button>
        </div>
        <div
          v-if="checkProgress"
          class="d-flex p-2"
        >
          <Loader />
        </div>
        <div v-else-if="checkError">
          <div class="alert alert-danger mt-2 small">
            {{ $t('vindication.unableToValidate') }}
          </div>
        </div>
        <div v-else-if="invalidTags.length > 0">
          <div class="alert alert-danger mt-2 small">
            {{ $t('vindication.invalidTags') }}:
            <span
              v-for="tag in invalidTags"
              :key="tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>
        <div v-else>
          <div class="alert alert-success mt-2 small">
            <i class="fas fa-check-circle mr-1" />
            {{ $t('vindication.validMessage') }}
          </div>
        </div>
        <div
          v-if="messageType !== 'email'"
          class="small text-secondary"
        >
          {{ $t('vindication.smsUsage') }}: {{ smsCost }}
        </div>

        <div
          v-if="showSaveTemplate"
          class="save-template-form mt-2 d-flex align-items-center"
        >
          <input
            v-model="templateName"
            :placeholder="$t('vindication.templateName')"
            class="form-control form-control-sm mr-2"
            style="max-width: 300px"
            @keyup.enter="saveTemplate"
          >
          <button
            class="btn btn-sm btn-primary mr-1"
            :disabled="savingTemplate"
            @click="saveTemplate"
          >
            {{ $t('general.save') }}
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="showSaveTemplate = false"
          >
            {{ $t('general.cancel') }}
          </button>
        </div>

        <div
          v-if="sendSuccess"
          class="alert alert-success mt-2 small"
        >
          {{ $t('general.sent') }}
        </div>
        <div
          v-if="sendError"
          class="alert alert-danger mt-2 small"
        >
          {{ $t('error.fail') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import BoxSelect from '@/components/BoxSelect';
import { mapActions, mapGetters } from 'vuex';
import axios from '@/api/axios';

export default {
  props: {
    payers: {
      type: Object,
      required: true,
    },
    sortKey: String,
    daysDelay: Number,
    group: String,
    categoryIds: Array,
  },
  data() {
    return {
      messageType: 'email',
      subject: '',
      editor: null,
      templates: [],
      selectedTemplateIdx: null,
      showSaveTemplate: false,
      templateName: '',
      sending: false,
      savingTemplate: false,
      sendSuccess: false,
      sendError: false,
      editorEmpty: true,
      smsCost: 0,
      invalidTags: [],
      checkProgress: false,
      checkError: false,
    };
  },
  components: {
    BoxSelect,
    EditorContent,
  },
  computed: {
    ...mapGetters(['formatMoney', 'payee', 'payeeProperty']),
    channelFields() {
      return [
        {
          key: 'email',
          icon: 'far fa-envelope',
          text: 'Email',
        },
        {
          key: 'phone',
          icon: 'far fa-comment-sms',
          text: 'SMS',
        },
      ];
    },
    payerList() {
      return Object.values(this.payers);
    },
    payerIds() {
      return Array.from(new Set(this.payerList.flatMap((p) => p.payerIds || [p.id])));
    },
    payerCount() {
      return this.payerIds.length;
    },
    allBills() {
      const seen = new Set();
      const bills = [];
      this.payerList.forEach((p) => {
        (p.bills || []).forEach((b) => {
          const key = `${b.payerId}_${b.billId}`;
          if (!seen.has(key)) {
            seen.add(key);
            bills.push(b);
          }
        });
      });
      return bills;
    },
    totalBillsCount() {
      return this.allBills.length;
    },
    totalAmountDue() {
      return this.allBills.reduce((sum, b) => sum + (b.amount - b.paid), 0);
    },
    hasBody() {
      return !this.editorEmpty;
    },
    canSend() {
      if (!this.hasBody) return false;
      if (this.messageType === 'email' && !this.subject.trim()) return false;
      return true;
    },
    filteredTemplates() {
      return this.templates.filter((t) => t.type === this.messageType);
    },
    storedTemplates() {
      return this.payeeProperty('VINDICATION_MESSAGES');
    },
  },
  watch: {
    storedTemplates: {
      immediate: true,
      handler(val) {
        if (!val) return;
        try {
          this.templates = JSON.parse(val);
        } catch {
          this.templates = [];
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'setPayeeProperties',
      'getPayeeProperties',
    ]),
    editorActive(check) {
      if (!this.editor) return false;
      if (typeof check === 'string') {
        return this.editor.isActive(check);
      }
      return this.editor.isActive(check);
    },
    editorCommand(cmd) {
      if (!this.editor) return;
      const chain = this.editor.chain().focus();
      switch (cmd) {
        case 'bold': chain.toggleBold().run(); break;
        case 'italic': chain.toggleItalic().run(); break;
        case 'underline': chain.toggleUnderline().run(); break;
        case 'bulletList': chain.toggleBulletList().run(); break;
        case 'orderedList': chain.toggleOrderedList().run(); break;
        case 'alignLeft': chain.setTextAlign('left').run(); break;
        case 'alignCenter': chain.setTextAlign('center').run(); break;
        case 'alignRight': chain.setTextAlign('right').run(); break;
        default: break;
      }
    },
    loadTemplate(tpl, idx) {
      this.selectedTemplateIdx = idx;
      this.subject = tpl.subject || '';
      if (this.editor) {
        this.editor.commands.setContent(tpl.body || '');
      }
    },
    async persistTemplates() {
      this.savingTemplate = true;
      try {
        await this.setPayeeProperties({
          data: {
            name: 'VINDICATION_MESSAGES',
            value: JSON.stringify(this.templates),
          },
        });
      } finally {
        this.savingTemplate = false;
      }
    },
    async renderCheck() {
      this.checkError = false;
      this.checkProgress = true;
      try {
        const body = this.messageType === 'email'
          ? this.editor.getHTML()
          : this.editor.getText();

        const { data } = await axios.post('/vindication/check', {
          listType: this.sortKey,
          ids: Object.keys(this.payers),
          deliveryType: this.messageType,
          subject: this.subject,
          body,
          daysDelay: this.daysDelay,
          group: this.group === '#all' ? null : this.group,
          categoryIds: this.categoryIds,
        });

        this.smsCost = data.smsCost;
        this.invalidTags = data.invalidTags;
      } catch (e) {
        this.checkError = true;
      } finally {
        this.checkProgress = false;
      }
    },
    async saveTemplate() {
      const body = this.editor ? this.editor.getHTML() : '';
      this.templates.push({
        type: this.messageType,
        name: this.templateName || this.subject || '',
        subject: this.subject,
        body,
      });
      await this.persistTemplates();
      this.showSaveTemplate = false;
      this.templateName = '';
    },
    async removeTemplate(idx) {
      const globalIdx = this.templates.indexOf(this.filteredTemplates[idx]);
      if (globalIdx >= 0) {
        this.templates.splice(globalIdx, 1);
      }
      if (this.selectedTemplateIdx === idx) {
        this.selectedTemplateIdx = null;
      }
      await this.persistTemplates();
    },
    clearEditor() {
      this.subject = '';
      if (this.editor) {
        this.editor.commands.clearContent();
      }
      this.selectedTemplateIdx = null;
    },
    async send() {
      this.sending = true;
      this.sendSuccess = false;
      this.sendError = false;

      if (!this.editor) {
        this.sendError = true;
        setTimeout(() => { this.sendError = false; }, 4000);
        return;
      }

      const body = this.messageType === 'email'
        ? this.editor.getHTML()
        : this.editor.getText();

      try {
        await axios.post('/vindication', {
          listType: this.sortKey,
          ids: Object.keys(this.payers),
          deliveryType: this.messageType,
          subject: this.subject,
          body,
          daysDelay: this.daysDelay,
          group: this.group === '#all' ? null : this.group,
          categoryIds: this.categoryIds,
        });

        this.sendSuccess = true;
        setTimeout(() => { this.sendSuccess = false; this.$emit('close'); }, 3000);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e, 'Failed to send vindication');
        this.sendError = true;
        setTimeout(() => { this.sendError = false; }, 4000);
      } finally {
        this.sending = false;
      }
    },
  },
  mounted() {
    if (this.sortKey === 'email') {
      this.messageType = 'email';
    } else if (this.sortKey === 'phone') {
      this.messageType = 'phone';
    }

    this.editor = new Editor({
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Placeholder.configure({
          placeholder: this.$t('vindication.bodyPlaceholder'),
        }),
      ],
      content: '',
      onUpdate: ({ editor }) => {
        this.editorEmpty = editor.isEmpty;
        if (this.timeout) {
          clearTimeout(this.timeout);
        }

        this.timeout = setTimeout(() => {
          this.renderCheck();
        }, 1000);
      },
    });

    this.getPayeeProperties({
      params: {
        query: {
          names: 'VINDICATION_MESSAGES',
        },
      },
    });
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
};
</script>

<style lang="scss" scoped>
.message-prepare {
  padding: 8px 0;
}

.summary-bar {
  gap: 24px;
}

.summary-item {
  text-align: center;
}

.summary-value {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
}

.summary-label {
  font-size: 11px;
}

.editor-layout {
  display: flex;
  gap: 16px;
  min-height: 400px;
}

.template-panel {
  width: 240px;
  flex-shrink: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow-y: auto;
  max-height: 600px;
}

.template-panel-header {
  font-weight: 600;
  font-size: 13px;
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.template-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  transition: background-color 150ms;

  &:hover {
    background-color: #f5f5f5;

    .template-delete {
      opacity: 1;
    }
  }

  &.active {
    background-color: rgba($blue, 0.08);
    border-left: 3px solid $blue;
  }
}

.template-name {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 24px;
}

.template-meta {
  font-size: 10px;
}

.template-delete {
  position: absolute;
  top: 6px;
  right: 4px;
  opacity: 0;
  color: #999;
  font-size: 12px;
  padding: 2px 4px;
  transition: opacity 150ms;
}

.editor-panel {
  flex: 1;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: #f8f9fa;
}

.toolbar-btn {
  border: none;
  background: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  color: #555;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms;

  &:hover {
    background-color: #e0e0e0;
  }

  &.active {
    background-color: rgba($blue, 0.15);
    color: $darkblue;
  }
}

.toolbar-separator {
  width: 1px;
  height: 20px;
  background: #ddd;
  margin: 0 4px;
}

.editor-content {
  border: 1px solid #e0e0e0;
  border-radius: 0 0 8px 8px;
  min-height: 250px;
  max-height: 500px;
  overflow-y: auto;

  ::v-deep .ProseMirror {
    padding: 12px 16px;
    min-height: 250px;
    outline: none;

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }

    ul, ol {
      padding-left: 1.5em;
    }

    p {
      margin: 0 0 0.4em;
    }
  }
}

.save-template-form {
  animation: fadeIn 150ms ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
